<template>
    <div class="admin-shell-sidebar-root" :class="rootClasses">
        <div
            v-if="isMobile && mobileOpen"
            class="tw:fixed tw:inset-0 tw:z-0 tw:bg-slate-950/70"
            data-testid="sidebar-backdrop"
            @click="closeMobile"
        ></div>

        <aside
            class="admin-shell-sidebar tw:flex tw:flex-col tw:h-full tw:w-full tw:rounded-xl tw:border tw:border-slate-800/80 tw:bg-[#0d1527] tw:text-slate-200 tw:overflow-hidden tw:transition-transform tw:duration-200 tw:ease-linear tw:select-none"
            :class="asideClasses"
            :style="clubVars"
            :data-collapsed="collapsed ? 'true' : 'false'"
            :data-mobile="isMobile ? 'true' : 'false'"
            :data-club-color="clubColor"
            data-testid="admin-sidebar"
        >
            <!-- Marca del club: fila de altura fija (el nav no se mueve al abrir/cerrar) -->
            <div class="tw:flex tw:h-[72px] tw:shrink-0 tw:items-center tw:border-b tw:border-slate-800/80 tw:px-2.5">
                <div class="tw:flex tw:w-full tw:min-w-0 tw:items-center tw:gap-3">
                    <router-link
                        v-if="clubLogo"
                        to="/"
                        class="tw:shrink-0 tw:rounded-md tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-slate-400"
                        aria-label="Ir al inicio"
                        data-testid="sidebar-brand-home"
                    >
                        <img
                            :src="clubLogo"
                            :alt="clubName"
                            class="tw:h-12 tw:w-auto tw:max-w-full tw:object-contain"
                        />
                    </router-link>
                    <i v-else class="fas fa-ticket-alt tw:shrink-0 tw:text-3xl tw:text-slate-300" aria-hidden="true"></i>
                    <div class="admin-brand-text tw:min-w-0 tw:flex-1 tw:text-left" :class="{ 'is-expanded': showLabels }">
                        <h2
                            v-if="clubName"
                            class="tw:m-0 tw:max-w-full tw:truncate tw:text-sm tw:font-bold tw:tracking-tight tw:text-white tw:leading-tight"
                            data-testid="sidebar-club-name"
                        >{{ clubName }}</h2>
                        <p v-if="clubSubtitle" class="tw:m-0 tw:max-w-full tw:truncate tw:text-[11px] tw:font-medium tw:text-slate-400">{{ clubSubtitle }}</p>
                    </div>
                </div>
            </div>

            <!-- Navegación -->
            <nav class="admin-sidebar-nav tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-2.5 tw:pt-3 tw:pb-1 tw:space-y-2.5 tw:text-[14px]">
                <AdminSidebarGroup
                    v-for="(group, index) in groups"
                    :key="group.key"
                    :group="group"
                    :collapsed="isRail"
                    :labels-visible="showLabels"
                    :show-separator="index > 0"
                    :active-keys="activeKeys"
                    :expanded-keys="expandedKeys"
                    @toggle="handleToggle"
                    @navigate="closeMobile"
                    @action="handleItemAction"
                />
            </nav>

            <!-- Pie: usuario logueado (mismo color que el body del sidebar) -->
            <div class="tw:shrink-0 tw:border-t tw:border-slate-800 tw:p-2.5">
                <AdminUserCard :compact="!showLabels" @logout="logout" />
            </div>
        </aside>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import store from '@/store';
    import { useRouter } from '@/composables/common/router/useRouter';
    import { useAdminNavigation } from '@/composables/admin/layout/useAdminNavigation';
    import { useAdminSidebar } from '@/composables/admin/layout/useAdminSidebar';
    import { useAdminClubIdentity } from '@/composables/admin/layout/useAdminClubIdentity';
    import AdminSidebarGroup from './AdminSidebarGroup.vue';
    import AdminUserCard from './AdminUserCard.vue';

    const router = useRouter();

    const { groups, activeKeys, expandedKeys, toggleExpanded } = useAdminNavigation({ tracking: true });
    const { collapsed, isMobile, mobileOpen, labelsVisible, closeMobile, expand } = useAdminSidebar();
    const { clubColor, clubTextColor } = useAdminClubIdentity();

    const manager = computed(() => store.getters.getFooter);
    const teatro = computed(() => store.getters.getTeatro);
    const configuracion = computed(() => store.getters.getConfig);
    const recinto = computed(() => store.getters.getRecinto);

    // El drawer mobile siempre muestra el sidebar completo, sin heredar el
    // estado de riel que quedó persistido en desktop.
    const isRail = computed(() => !isMobile.value && collapsed.value);
    const showLabels = computed(() => isMobile.value || labelsVisible.value);

    const clubLogo = computed(() => (manager.value && manager.value.logo) || null);
    const clubName = computed(() => {
        const candidates = [
            manager.value && manager.value.nombre,
            teatro.value && teatro.value.nombre,
            configuracion.value && configuracion.value.title
        ];
        return candidates.find(value => !!value) || '';
    });
    const clubSubtitle = computed(() => {
        if (!manager.value) return '';
        const kind = manager.value.tipo == 2 ? 'Teatro' : 'Club';
        const venue = recinto.value && recinto.value.nombre;
        return venue ? kind + ' · ' + venue : kind;
    });

    const clubVars = computed(() => ({
        '--admin-club-color': clubColor.value,
        '--admin-club-fg': clubTextColor.value
    }));

    /*
     * El transform de apertura/cierre vive en el <aside>, NUNCA en el root:
     * un ancestro con transform convertiría al backdrop `position: fixed`
     * en relativo al aside (y dejaría de cubrir el viewport).
     *
     * tw:static / tw:p-0 NO son redundantes: public/css/main2.css:1134 define
     * `aside { position: fixed; left: 0; width: 200px; padding-top: 9rem;
     * height: 100%; z-index: 100; ... }` (stylesheet global legacy) y
     * secuestraría este <aside> en desktop (fixed + width 100% del viewport).
     * Las utilities de Tailwind (importantes) lo neutralizan explícitamente.
     */
    const rootClasses = computed(() => {
        if (isMobile.value) {
            return [
                'tw:fixed tw:top-3 tw:bottom-3 tw:left-3 tw:z-50 tw:w-60',
                mobileOpen.value ? '' : 'tw:pointer-events-none'
            ].join(' ');
        }
        return [
            'tw:flex tw:shrink-0 tw:mr-3 tw:md:mr-4 tw:transition-[width] tw:duration-200 tw:ease-linear',
            collapsed.value ? 'tw:w-[68px]' : 'tw:w-60'
        ].join(' ');
    });

    const asideClasses = computed(() => {
        if (!isMobile.value) return 'tw:static tw:p-0';
        return mobileOpen.value
            ? 'tw:relative tw:z-10 tw:p-0 tw:translate-x-0 tw:shadow-2xl'
            : 'tw:relative tw:z-10 tw:p-0 tw:-translate-x-[125%] tw:shadow-2xl';
    });

    function handleToggle(key) {
        // En modo riel, un toggle de submenú expande el sidebar primero.
        if (isRail.value) expand();
        toggleExpanded(key);
    }

    function handleItemAction(action) {
        if (action === 'logout') logout();
        closeMobile();
    }

    function logout() {
        store.dispatch('logout', { router });
    }
</script>

<style scoped>
    .admin-brand-text {
        opacity: 0;
        visibility: hidden;
        overflow: hidden;
        transition: opacity 0.12s ease, visibility 0s linear 0.12s;
    }

    .admin-brand-text.is-expanded {
        opacity: 1;
        visibility: visible;
        transition: opacity 0.12s ease, visibility 0s;
    }

    /* Barra oculta: el nav sigue scrolleando con rueda/touch/teclado. */
    .admin-sidebar-nav {
        scrollbar-width: none;
    }

    .admin-sidebar-nav::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
</style>
